import {
  employeeConfirmationService,
  employeeOnboardingService,
  employeeSeparationService,
  employeeTransferService,
  helpdeskService,
  attendanceStructureService,
  attendanceService,
  assetService,
  flexiBenefitsService,
} from "../services/api";
import {
  mapOnboardingChecklistToDeskItem,
  mapOnboardingMyRequestToDeskItem,
  mapOnboardingRequestToDeskItem,
  collapseDuplicateOnboardingChecklistRows,
} from "./onboardingApprovalDesk";
import { mapLifecycleDeskApiRows } from "./lifecycleApprovalDesk";
import { buildHelpdeskDeskItems } from "./helpdeskTicketDesk";
import { isOnboardingNotifyAcknowledged } from "./onboardingNotifyAck";
import {
  buildLeaveDeskItems,
  displayLeaveActionLabel,
  isLeaveDeskRequest,
  leaveRequestIdFromDeskId,
} from "./leaveRequestDesk";
import { buildAssetDeskItems } from "./assetDesk";
import {
  buildFlexiReimbursementDeskItems,
  flexiClaimIdFromDeskId,
  isFlexiClaimDeskRequest,
} from "./flexiReimbursementDesk";

function normalizeStatusKey(status) {
  const s = String(status || "").trim();
  if (s === "Undo" || s === "Recall") return "Undo";
  if (s === "Cancelled") return "Cancelled";
  if (s === "Approved" || s === "Rejected" || s === "Pending") return s;
  return "Pending";
}

function deskItemToConsoleRow(item, moduleKey) {
  const status = normalizeStatusKey(item.status);
  const pendingWithRaw =
    status === "Pending" ? item.pendingWith || item.assigneeDisplay || null : null;
  const pendingWith =
    pendingWithRaw &&
    String(pendingWithRaw).trim().toLowerCase() !== "pending"
      ? pendingWithRaw
      : status === "Pending"
        ? item.pendingLabel || item.pendingRole || "Awaiting workflow assignment"
        : null;
  return {
    ...item,
    id: item.requestId,
    module: moduleKey,
    actionLabel: displayLeaveActionLabel(item.actionLabel),
    userName: item.userName || "-",
    employeeCode: item.employeeCode || item.employeeId || "",
    userEmail: item.userEmail || "",
    status,
    pendingWith,
    pendingAssigneeDisplay: item.pendingAssigneeDisplay || null,
    pendingLabel: item.pendingLabel || null,
    pendingRole: item.pendingRole || null,
    canAct: item.canAct === true || item.canApprove === true,
    canApprove: item.canApprove === true,
    submittedAt: item.submittedAt || null,
    updatedAt: item.updatedAt || null,
    formData: item.formData || {},
    workflowTrail: item.workflowTrail || item.workflowSteps || [],
    slaDays: item.slaDays ?? 3,
    source: item.source || "api",
  };
}

/**
 * Load Approvals-desk + My-requests desk rows for Activity Console.
 * My-requests are included so pending onboardings the admin raised (and can act on)
 * appear here — Employee Insights was replaced by Activity Console.
 */
export async function loadActivityConsoleApiRows() {
  const rows = [];

  try {
    const [approvalsRes, myRes] = await Promise.all([
      employeeOnboardingService.listApprovalsDesk().catch(() => ({ data: [] })),
      employeeOnboardingService.listMyRequestsDesk().catch(() => ({ data: [] })),
    ]);
    const onboardingRows = collapseDuplicateOnboardingChecklistRows(
      Array.isArray(approvalsRes?.data) ? approvalsRes.data : []
    );
    const seenOnboardingIds = new Set();

    onboardingRows.forEach((row) => {
      const mapped =
        row.deskMode === "checklist"
          ? mapOnboardingChecklistToDeskItem(row)
          : mapOnboardingRequestToDeskItem(row, {
              notifyOnlyFyi: !!row.notifyOnlyFyi,
              acknowledged: isOnboardingNotifyAcknowledged(
                `onboarding_${row.id}${row.notifyOnlyFyi ? "_notify" : ""}`
              ),
            });
      seenOnboardingIds.add(Number(row.id));
      rows.push(deskItemToConsoleRow(mapped, "employee"));
    });

    const myRows = Array.isArray(myRes?.data) ? myRes.data : [];
    myRows.forEach((row) => {
      // Prefer Approvals-desk copy when both exist; still surface my-request
      // when Approvals is empty (draft / broken chain) so Activity Console is not blank.
      // If the owner can act (RM / Admin), mapOnboardingMyRequestToDeskItem promotes
      // it to an Approval so Admins are not stuck with Recall-only My Request.
      if (seenOnboardingIds.has(Number(row.id))) return;
      const mapped = mapOnboardingMyRequestToDeskItem(row);
      seenOnboardingIds.add(Number(row.id));
      rows.push(deskItemToConsoleRow(mapped, "employee"));
    });
  } catch (err) {
    console.warn("Activity Console: onboarding desk load failed", err);
  }

  try {
    const [confApprovals, xferApprovals, sepApprovals] = await Promise.all([
      employeeConfirmationService.listApprovalsDesk().catch(() => ({ data: [] })),
      employeeTransferService.listApprovalsDesk().catch(() => ({ data: [] })),
      employeeSeparationService.listApprovalsDesk().catch(() => ({ data: [] })),
    ]);
    const approvalRows = [
      ...(Array.isArray(confApprovals?.data) ? confApprovals.data : []),
      ...(Array.isArray(xferApprovals?.data) ? xferApprovals.data : []),
      ...(Array.isArray(sepApprovals?.data) ? sepApprovals.data : []),
    ];
    mapLifecycleDeskApiRows(approvalRows).forEach((mapped) => {
      rows.push(deskItemToConsoleRow(mapped, "employee"));
    });
  } catch (err) {
    console.warn("Activity Console: lifecycle desk load failed", err);
  }

  try {
    const res = await helpdeskService.listDeskTickets().catch(() => ({ data: {} }));
    const pack = res?.data || {};
    buildHelpdeskDeskItems({
      mine: pack.mine || [],
      pendingMyAction: pack.pendingMyAction || [],
    }).forEach((mapped) => {
      if (mapped.panelKey === "approvals" || mapped.status === "Pending") {
        rows.push(deskItemToConsoleRow(mapped, "helpdesk"));
      } else {
        rows.push(deskItemToConsoleRow(mapped, "helpdesk"));
      }
    });
  } catch (err) {
    console.warn("Activity Console: helpdesk desk load failed", err);
  }

  try {
    const [pendingRes, allRes] = await Promise.all([
      attendanceStructureService
        .getPendingLeaveRequestsForUser()
        .catch(() => ({ data: [] })),
      attendanceStructureService
        .getLeaveRequests({ limit: 200 })
        .catch(() => ({ data: [] })),
    ]);
    const pendingMyAction = Array.isArray(pendingRes?.data) ? pendingRes.data : [];
    const allLeave = Array.isArray(allRes?.data) ? allRes.data : [];
    const pendingIds = new Set(
      pendingMyAction.map((r) => String(r.id ?? r.requestId ?? ""))
    );
    // Company-wide leave for Admin oversight; mark canAct from pending-my-action where applicable.
    const companyRows = allLeave.map((row) => {
      const id = String(row.id ?? "");
      if (pendingIds.has(id) && row.canAct == null) {
        return { ...row, canAct: true };
      }
      return row;
    });
    buildLeaveDeskItems({
      mine: companyRows,
      pendingMyAction,
    }).forEach((mapped) => {
      const leaveId =
        leaveRequestIdFromDeskId(mapped.requestId) ||
        leaveRequestIdFromDeskId(mapped.leaveRequestId) ||
        mapped.leaveRequestId;
      rows.push(
        deskItemToConsoleRow(
          {
            ...mapped,
            requestId: `leave_req_${leaveId}`,
            leaveRequestId: leaveId,
            canApprove: mapped.canAct === true,
          },
          "attendance"
        )
      );
    });
  } catch (err) {
    console.warn("Activity Console: leave desk load failed", err);
  }

  try {
    const res = await assetService.listDesk().catch(() => ({ data: {} }));
    const pack = res?.data || res || {};
    buildAssetDeskItems({
      pendingRequests: pack.pendingRequests || [],
      pendingRecoveries: pack.pendingRecoveries || [],
      overdueReturns: pack.overdueReturns || [],
    }).forEach((mapped) => {
      rows.push(deskItemToConsoleRow(mapped, "assets"));
    });
  } catch (err) {
    console.warn("Activity Console: assets desk load failed", err);
  }

  try {
    const [claimsRes, declRes] = await Promise.all([
      flexiBenefitsService.listClaims({}).catch(() => ({ data: [] })),
      flexiBenefitsService.listDeclarations({}).catch(() => ({ data: [] })),
    ]);
    let employeeId = null;
    let userId = null;
    try {
      const me = JSON.parse(localStorage.getItem("currentUser") || "{}");
      userId = me?.id || null;
      employeeId =
        Number(localStorage.getItem("employeeId")) ||
        Number(me?.employeeId) ||
        null;
    } catch {
      /* ignore */
    }
    buildFlexiReimbursementDeskItems({
      claims: Array.isArray(claimsRes?.data) ? claimsRes.data : [],
      declarations: Array.isArray(declRes?.data) ? declRes.data : [],
      employeeId,
      userId,
    }).forEach((mapped) => {
      rows.push(
        deskItemToConsoleRow(
          {
            ...mapped,
            canApprove: mapped.canAct === true,
          },
          "payroll"
        )
      );
    });
  } catch (err) {
    console.warn("Activity Console: reimbursement desk load failed", err);
  }

  return rows;
}

/**
 * Run Approve / Reject / Done / Acknowledge / Resolve against the correct API.
 */
export async function runActivityConsoleDeskAction(request, nextStatus, remarks) {
  if (!request) throw new Error("Request not found");

  if (request.notifyOnlyFyi && (nextStatus === "Acknowledged" || nextStatus === "Approved")) {
    const { acknowledgeOnboardingNotify } = await import("./onboardingNotifyAck");
    acknowledgeOnboardingNotify(request.requestId);
    return { message: "Marked as viewed — no approval action required" };
  }

  const rid = String(request.requestId || "");

  if (
    (nextStatus === "Submitted" || nextStatus === "Submit") &&
    (rid.startsWith("onboarding_") || rid.startsWith("onboarding_req_")) &&
    !rid.startsWith("onboarding_chk_")
  ) {
    if (!request.onboardingRequestId) throw new Error("Missing onboarding request id");
    const res = await employeeOnboardingService.submitRequest(request.onboardingRequestId);
    if (res?.autoApproved || res?.alreadyCompleted) {
      return {
        message: res?.notifyOnlyAutoApproved
          ? "Employee created — reporting manager notified (no approval required)"
          : "Employee onboarded successfully",
      };
    }
    const pending = res?.data?.pendingLabel || res?.data?.pendingRole || "approver";
    return { message: `Submitted for approval — pending with ${pending}` };
  }

  if (rid.startsWith("onboarding_chk_")) {
    if (!request.onboardingRequestId || !request.checklistTaskId) {
      throw new Error("Missing onboarding checklist ids");
    }
    if (nextStatus === "Approved") {
      await employeeOnboardingService.completeChecklistTask(
        request.onboardingRequestId,
        request.checklistTaskId,
        remarks
      );
      return { message: "Onboarding task marked Done" };
    }
    if (nextStatus === "Rejected") {
      await employeeOnboardingService.rejectChecklistTask(
        request.onboardingRequestId,
        request.checklistTaskId,
        remarks
      );
      return { message: "Onboarding task rejected" };
    }
  }

  if (
    (rid.startsWith("onboarding_") || rid.startsWith("onboarding_req_")) &&
    !rid.startsWith("onboarding_chk_")
  ) {
    if (!request.onboardingRequestId) throw new Error("Missing onboarding request id");
    if (nextStatus === "Approved") {
      await employeeOnboardingService.approveRequest(request.onboardingRequestId, remarks);
      return { message: "Onboarding step approved" };
    }
    if (nextStatus === "Rejected") {
      await employeeOnboardingService.rejectRequest(request.onboardingRequestId, remarks);
      return { message: "Onboarding request rejected" };
    }
  }

  if (
    rid.startsWith("confirmation_") ||
    rid.startsWith("transfer_") ||
    rid.startsWith("separation_")
  ) {
    const kind = request.lifecycleKind;
    const service =
      kind === "confirmation"
        ? employeeConfirmationService
        : kind === "transfer"
          ? employeeTransferService
          : kind === "separation"
            ? employeeSeparationService
            : null;
    if (!service) throw new Error("Unknown lifecycle kind");
    const id =
      request.confirmationRequestId ||
      request.transferRequestId ||
      request.separationRequestId;
    if (!id) throw new Error("Missing lifecycle request id");

    if (request.deskMode === "checklist") {
      const itemId = request.checklistItemId || request.checklistTaskId;
      if (!itemId) throw new Error("Missing checklist item id");
      if (nextStatus === "Approved") {
        await service.completeChecklistItem(id, itemId, {
          remarks,
          comments: remarks,
        });
        return {
          message: /letter/i.test(request.actionLabel || "")
            ? "Letter task marked generated"
            : "Checklist task completed",
        };
      }
      throw new Error("Reject checklist from Employee Insights if not supported here");
    }

    if (nextStatus === "Approved") {
      await service.approve(id, { comments: remarks, remarks });
      return { message: `${kind} step approved` };
    }
    if (nextStatus === "Rejected") {
      await service.reject(id, { comments: remarks, remarks });
      return { message: `${kind} rejected` };
    }
  }

  if (rid.startsWith("helpdesk_") || request.deskMode === "helpdesk") {
    const ticketId = request.helpdeskTicketId || request.formData?.ticketId;
    if (!ticketId) throw new Error("Missing ticket id");
    if (nextStatus === "Approved") {
      await helpdeskService.closeTicket(ticketId, {
        asResolved: true,
        resolutionNotes: remarks,
      });
      return { message: "Ticket resolved" };
    }
    if (nextStatus === "Rejected") {
      await helpdeskService.closeTicket(ticketId, {
        asResolved: false,
        resolutionNotes: remarks,
        rcaJson: { rejected: true, rejectionReason: remarks },
      });
      return { message: "Ticket closed as rejected" };
    }
  }

  if (rid.startsWith("flexi_claim_") || isFlexiClaimDeskRequest(request)) {
    const numericId =
      flexiClaimIdFromDeskId(rid) || request.flexiClaimId || null;
    if (!numericId) throw new Error("Missing reimbursement claim id");
    if (nextStatus === "Approved") {
      await flexiBenefitsService.decideClaim(numericId, {
        decision: "APPROVED",
        remarks,
      });
      return { message: "Reimbursement claim approved" };
    }
    if (nextStatus === "Rejected") {
      await flexiBenefitsService.decideClaim(numericId, {
        decision: "REJECTED",
        remarks,
      });
      return { message: "Reimbursement claim rejected" };
    }
    if (nextStatus === "Query") {
      await flexiBenefitsService.decideClaim(numericId, {
        decision: "CLARIFICATION",
        remarks,
      });
      return { message: "Query raised on reimbursement claim" };
    }
  }

  if (isLeaveDeskRequest(request) || request.deskMode === "leave") {
    const numericId =
      leaveRequestIdFromDeskId(request.requestId) ||
      Number(request.leaveRequestId);
    if (!numericId) throw new Error("Missing leave request id");
    if (nextStatus === "Approved") {
      const res = await attendanceStructureService.approveLeaveRequest(
        numericId,
        remarks
      );
      return {
        message:
          res?.data?.status === "approved"
            ? "Leave request fully approved"
            : `Approved — pending at ${res?.data?.pendingAt || "next approver"}`,
      };
    }
    if (nextStatus === "Rejected") {
      await attendanceStructureService.rejectLeaveRequest(numericId, remarks);
      return { message: "Leave request rejected" };
    }
    if (nextStatus === "Undo") {
      await attendanceStructureService.cancelLeaveRequest(numericId, remarks);
      return { message: "Leave request undone" };
    }
    if (nextStatus === "CancelRequested") {
      const res = await attendanceStructureService.requestLeaveCancellation(
        numericId,
        remarks
      );
      return {
        message: res?.autoApproved
          ? "Leave cancelled"
          : "Leave cancellation request submitted",
      };
    }
  }

  if (rid.startsWith("asset_req_") || request.deskMode === "asset_request") {
    const id = request.assetRequestId || rid.replace("asset_req_", "");
    if (!id) throw new Error("Missing asset request id");
    if (nextStatus === "Approved") {
      await assetService.approveRequest(id, { remarks });
      return { message: "Asset request approved" };
    }
    if (nextStatus === "Rejected") {
      await assetService.rejectRequest(id, { reason: remarks, remarks });
      return { message: "Asset request rejected" };
    }
  }

  if (rid.startsWith("asset_recovery_") || request.deskMode === "asset_recovery") {
    const id = request.assetRecoveryId || rid.replace("asset_recovery_", "");
    if (!id) throw new Error("Missing asset recovery id");
    if (nextStatus === "Approved") {
      await assetService.approveRecovery(id, { remarks });
      await assetService.sendRecoveryToFnf(id, { remarks }).catch(() => null);
      return { message: "Asset recovery approved and sent to F&F" };
    }
    if (nextStatus === "Rejected") {
      await assetService.waiveRecovery(id, { reason: remarks, remarks });
      return { message: "Asset recovery waived" };
    }
  }

  if (rid.startsWith("asset_return_") || request.deskMode === "asset_return") {
    const assetId = request.assetId || request.formData?.assetId;
    if (!assetId) throw new Error("Missing asset id for return");
    if (nextStatus === "Approved") {
      await assetService.returnAsset(assetId, { remarks, notes: remarks });
      return { message: "Asset marked returned" };
    }
  }

  if (nextStatus === "CancelRequested") {
    const id = request.serverDeskId || request.requestId;
    if (!id) throw new Error("Missing desk request id");
    const res = await attendanceService.requestDeskCancellation(id, remarks);
    return {
      message: res?.autoApproved
        ? "Request cancelled"
        : "Cancellation request submitted",
    };
  }

  if (nextStatus === "Undo") {
    const label = String(request?.actionLabel || "").trim();
    const attendanceLabels = new Set([
      "Regularise checks",
      "Restricted Holiday",
      "Comp Off",
      "Comp Off Work",
      "Comp Off Credit",
      "Overtime",
      "Change WO",
      "On Duty",
      "Short Leave",
      "Attendance Lock",
      "Attendance Unlock",
      "Attendance Finalization",
    ]);
    if (attendanceLabels.has(label)) {
      const { syncDeskRequestRecall } = await import("../lib/compOffOtNotify");
      const ok = await syncDeskRequestRecall(request, remarks);
      return {
        message: ok ? "Request recalled" : "Request recalled (local only)",
      };
    }
  }

  if (nextStatus === "Approved" || nextStatus === "Rejected") {
    const current = String(request?.status || "");
    if (current === "Approved" && nextStatus === "Approved") {
      throw new Error("This request is already approved.");
    }
    if (current === "Rejected" && nextStatus === "Rejected") {
      throw new Error("This request is already rejected.");
    }
    const label = String(request?.actionLabel || "").trim();
    if (
      label === "Change WO" ||
      label === "Change Shift" ||
      label === "Short Leave" ||
      label === "On Duty" ||
      label === "Attendance Lock" ||
      label === "Attendance Unlock" ||
      label === "Attendance Finalization"
    ) {
      const id = request.serverDeskId || request.requestId;
      if (!id) throw new Error(`${label} request is missing a server id`);
      const { getAuthJson } = await import("../utils/authStorage");
      const currentUser = getAuthJson("currentUser", null);
      const firstName = currentUser?.name || "";
      const lastName = currentUser?.lastName || "";
      const actorName = [firstName, lastName].filter(Boolean).join(" ") || null;
      await attendanceService.updateDeskQuickRequestStatus(id, nextStatus, {
        reason: remarks || request.actionRemarks || null,
        actorName,
      });
      return { message: `${label} ${String(nextStatus).toLowerCase()}` };
    }
    if (label === "Regularise checks") {
      const { syncRegulariseDecision } = await import("../lib/compOffOtNotify");
      await syncRegulariseDecision(request, nextStatus);
      return { message: `Regularise checks ${String(nextStatus).toLowerCase()}` };
    }
    if (label === "Restricted Holiday") {
      const { syncRestrictedHolidayDecisionAndNotify } = await import(
        "../lib/restrictedHolidayNotify"
      );
      await syncRestrictedHolidayDecisionAndNotify(request, nextStatus);
      return { message: `Restricted Holiday ${String(nextStatus).toLowerCase()}` };
    }
    if (label === "Comp Off" || label === "Comp Off Work" || label === "Comp Off Credit") {
      const { syncCompOffDecision } = await import("../lib/compOffOtNotify");
      await syncCompOffDecision(request, nextStatus);
      return { message: `${label} ${String(nextStatus).toLowerCase()}` };
    }
    if (label === "Overtime") {
      const { syncOvertimeDecision } = await import("../lib/compOffOtNotify");
      await syncOvertimeDecision(request, nextStatus);
      return { message: `Overtime ${String(nextStatus).toLowerCase()}` };
    }
  }

  throw new Error("Unsupported action for this request type");
}
